import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Search,
  Filter,
  ChevronLeft,
  ChevronRight,
  Activity,
  Clock,
} from 'lucide-react';
import apiClient from '@/api/client';

interface AuditLog {
  id: string;
  timestamp: string;
  user_id: string;
  user_name: string;
  user_email: string;
  action: string;
  resource_type: string;
  resource_id: string;
  description: string;
  ip_address: string;
  user_agent: string;
  level: string;
  metadata: Record<string, unknown>;
}

interface AuditLogsResponse {
  data: AuditLog[];
  meta?: {
    total: number;
    total_pages: number;
    page: number;
    limit: number;
  };
}

const RESOURCE_TYPES = [
  '',
  'exam',
  'question',
  'question_bank',
  'user',
  'tenant',
  'attempt',
  'enrollment',
  'certificate',
  'notification',
  'announcement',
  'backup',
  'settings',
];

export const ActivityLogsPage = () => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [resourceType, setResourceType] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['audit-logs', page, search, resourceType, dateFrom, dateTo],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', '20');
      if (search) params.append('search', search);
      if (resourceType) params.append('resource_type', resourceType);
      if (dateFrom) params.append('start_date', dateFrom);
      if (dateTo) params.append('end_date', dateTo);
      const res = await apiClient.get<AuditLogsResponse>(`/audit-logs?${params}`);
      return res.data;
    },
  });

  const logs: AuditLog[] = Array.isArray(data?.data) ? data.data : [];
  const totalPages = data?.meta?.total_pages || 1;
  const total = data?.meta?.total || 0;

  const formatTimestamp = (ts: string) => {
    try {
      const date = new Date(ts);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
    } catch {
      return ts;
    }
  };

  const getLevelBadge = (level: string) => {
    switch (level) {
      case 'error':
        return 'bg-red-500/20 text-red-400';
      case 'warning':
        return 'bg-amber-500/20 text-amber-400';
      default:
        return 'bg-teal-500/20 text-teal-400';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white/90">Activity Logs</h1>
          <p className="text-sm text-white/50 mt-1">
            Monitor all system activities and user actions
          </p>
        </div>
        <div className="flex items-center space-x-2 text-white/40">
          <Activity className="w-5 h-5" />
          <span className="text-sm">{total} total entries</span>
        </div>
      </div>

      {/* Filters */}
      <div className="card-dark p-4">
        <div className="flex items-center space-x-2 mb-3">
          <Filter className="w-4 h-4 text-white/40" />
          <span className="text-sm text-white/60 font-medium">Filters</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
            <input
              type="text"
              placeholder="Search by action or user..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              className="input-dark w-full pl-9"
            />
          </div>

          {/* Resource Type */}
          <select
            value={resourceType}
            onChange={(e) => {
              setResourceType(e.target.value);
              setPage(1);
            }}
            className="input-dark w-full"
          >
            <option value="">All Resource Types</option>
            {RESOURCE_TYPES.filter(Boolean).map((type) => (
              <option key={type} value={type}>
                {type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
              </option>
            ))}
          </select>

          {/* Date From */}
          <div>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => {
                setDateFrom(e.target.value);
                setPage(1);
              }}
              className="input-dark w-full"
              placeholder="From date"
            />
          </div>

          {/* Date To */}
          <div>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => {
                setDateTo(e.target.value);
                setPage(1);
              }}
              className="input-dark w-full"
              placeholder="To date"
            />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card-dark overflow-hidden">
        {isLoading ? (
          <div className="p-12 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-400 mx-auto mb-3" />
            <p className="text-sm text-white/50">Loading activity logs...</p>
          </div>
        ) : logs.length === 0 ? (
          <div className="p-12 text-center">
            <Clock className="w-10 h-10 text-white/20 mx-auto mb-3" />
            <p className="text-white/60">No activity logs found</p>
            <p className="text-xs text-white/40 mt-1">
              Try adjusting your filters or date range
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="table-dark w-full text-sm">
              <thead>
                <tr className="border-b border-white/[0.06]">
                  <th className="px-4 py-3 text-left text-xs font-medium text-white/50 uppercase tracking-wider">
                    Timestamp
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white/50 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white/50 uppercase tracking-wider">
                    Action
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white/50 uppercase tracking-wider">
                    Resource Type
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white/50 uppercase tracking-wider">
                    Resource ID
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white/50 uppercase tracking-wider">
                    IP Address
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white/50 uppercase tracking-wider">
                    Details
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/[0.04]">
                {logs.map((log) => (
                  <tr
                    key={log.id}
                    className="hover:bg-white/[0.02] transition-colors"
                  >
                    <td className="px-4 py-3 whitespace-nowrap text-white/60">
                      <div className="flex items-center space-x-2">
                        <Clock className="w-3.5 h-3.5 text-white/30 flex-shrink-0" />
                        <span className="text-xs">{formatTimestamp(log.timestamp)}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div>
                        <p className="text-white/80 text-xs font-medium">
                          {log.user_name || 'Unknown'}
                        </p>
                        {log.user_email && (
                          <p className="text-white/40 text-xs">{log.user_email}</p>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span
                        className={`px-2 py-0.5 rounded text-xs font-medium ${getLevelBadge(log.level)}`}
                      >
                        {log.action}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-white/60 text-xs">
                      {log.resource_type
                        ? log.resource_type
                            .replace(/_/g, ' ')
                            .replace(/\b\w/g, (c) => c.toUpperCase())
                        : '-'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-white/50 text-xs font-mono">
                      {log.resource_id
                        ? log.resource_id.length > 12
                          ? `${log.resource_id.slice(0, 12)}...`
                          : log.resource_id
                        : '-'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-white/50 text-xs font-mono">
                      {log.ip_address || '-'}
                    </td>
                    <td className="px-4 py-3 text-white/50 text-xs max-w-xs truncate">
                      {log.description || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {logs.length > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-white/[0.06]">
            <p className="text-xs text-white/40">
              Page {page} of {totalPages} ({total} total)
            </p>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="btn-primary px-3 py-1.5 rounded-lg text-xs flex items-center space-x-1 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-3.5 h-3.5" />
                <span>Previous</span>
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="btn-primary px-3 py-1.5 rounded-lg text-xs flex items-center space-x-1 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <span>Next</span>
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
